import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import * as L from 'leaflet';
import { PopSummary } from '../../../services/service-delivery-locations.service';

@Component({
  selector: 'app-service-delivery-location-map',
  templateUrl: './service-delivery-location-map.component.html',
  styleUrls: ['./service-delivery-location-map.component.scss']
})
export class ServiceDeliveryLocationMapComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() pops: PopSummary[] = [];
  @Input() selectedPopId: number | null = null;
  @Input() loading = false;
  @Input() visible = true;
  @Input() height = 480;
  @Output() selectPop = new EventEmitter<PopSummary>();

  @ViewChild('mapContainer', { static: true }) mapContainer?: ElementRef<HTMLDivElement>;

  private mapInstance?: L.Map;
  private markerLayer = L.layerGroup();
  private readonly fallbackBounds = L.latLngBounds([[-45, -150], [65, 150]]);

  private readonly tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    attribution: '&copy; OpenStreetMap contributors'
  });

  private readonly defaultIcon = L.icon({
    iconRetinaUrl: 'assets/leaflet/marker-icon-2x.png',
    iconUrl: 'assets/leaflet/marker-icon.png',
    shadowUrl: 'assets/leaflet/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [0, -28],
    shadowSize: [41, 41]
  });

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    if (!this.mapContainer) {
      return;
    }
    this.mapInstance = L.map(this.mapContainer.nativeElement, {
      zoom: 2,
      center: L.latLng(25, 0),
      layers: [this.tileLayer, this.markerLayer]
    });
    setTimeout(() => this.mapInstance?.invalidateSize(), 0);
    this.rebuildMarkers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue && this.mapInstance) {
      setTimeout(() => this.mapInstance?.invalidateSize(), 0);
    }
    if ((changes['pops'] || changes['selectedPopId']) && this.mapInstance) {
      this.rebuildMarkers();
    }
  }

  ngOnDestroy(): void {
    if (this.mapInstance) {
      this.mapInstance.remove();
      this.mapInstance = undefined;
    }
    this.markerLayer.clearLayers();
  }

  private rebuildMarkers(): void {
    if (!this.mapInstance) {
      return;
    }
    this.markerLayer.clearLayers();

    const markers: L.Marker[] = [];
    let selectedMarker: L.Marker | null = null;

    this.pops.forEach((pop) => {
      if (!this.hasCoordinates(pop)) {
        return;
      }

      const marker = L.marker([pop.latitude!, pop.longitude!], {
        icon: this.defaultIcon,
        title: pop.name,
        riseOnHover: true
      });

      const popupLines = [
        `<strong>${pop.displayId || pop.name}</strong>`,
        pop.name && pop.displayId ? `<div>${pop.name}</div>` : '',
        `<div>${pop.city || pop.region || ''} ${pop.country || ''}</div>`,
        this.buildServicesLine(pop),
        this.buildCrossConnectSection(pop),
        `<div class="popup-actions"><a href="#" class="popup-pop-details-link">See PoP details</a></div>`
      ];
      marker.bindPopup(popupLines.filter(Boolean).join(''));
      marker.on('popupopen', (event) => this.attachPopupListeners(event.popup, pop));
      const badgeLabel = pop.displayId || pop.name || '-';
      marker.bindTooltip(badgeLabel, {
        permanent: true,
        direction: 'top',
        offset: [0, -5],
        opacity: 1,
        className: `pop-badge-tooltip ${this.getBadgeClass(pop)}`
      });

      if (pop.id === this.selectedPopId) {
        selectedMarker = marker;
      }

      markers.push(marker);
      marker.addTo(this.markerLayer);
    });

    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(marker => marker.getLatLng())).pad(0.15);
      this.mapInstance.fitBounds(bounds, { padding: [30, 30], maxZoom: 10 });
      if (selectedMarker) {
        setTimeout(() => selectedMarker?.openPopup(), 0);
      }
    } else {
      this.mapInstance.fitBounds(this.fallbackBounds);
    }
  }

  private hasCoordinates(pop: PopSummary): pop is PopSummary & { latitude: number; longitude: number } {
    return typeof pop.latitude === 'number' && typeof pop.longitude === 'number';
  }

  private buildCrossConnectSection(pop: PopSummary): string {
    if (!pop.crossConnects?.length) {
      return '<div class="popup-xco"><span class="label">0 XCOs:</span> <span>N/A</span></div>';
    }

    const labels = new Set<string>();
    const entries: string[] = [];
    pop.crossConnects.forEach(cc => {
      if (!cc.crossConnectId) {
        return;
      }
      if (labels.has(cc.crossConnectId)) {
        return;
      }
      labels.add(cc.crossConnectId);
      const tp = cc.technicalPop?.technicalId;
      const label = tp ? `${cc.crossConnectId} (${tp})` : cc.crossConnectId;
      entries.push(`<span class="popup-xco-entry">${label}</span>`);
    });

    const count = entries.length;
    if (count === 0) {
      return '<div class="popup-xco"><span class="label">0 XCOs:</span> <span>N/A</span></div>';
    }

    const countLabel = `${count} XCO${count === 1 ? '' : 's'}`;
    return `<div class="popup-xco"><span class="label">${countLabel}:</span> ${entries.join(', ')}</div>`;
  }

  private attachPopupListeners(popup?: L.Popup, pop?: PopSummary): void {
    const element = popup?.getElement();
    if (!element || !pop) {
      return;
    }
    const detailLinks = element.querySelectorAll<HTMLAnchorElement>('.popup-pop-details-link');
    detailLinks.forEach(link => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.ngZone.run(() => this.selectPop.emit(pop));
        this.mapInstance?.closePopup();
      });
    });
  }

  private buildServicesLine(pop: PopSummary): string {
    const count = pop.services?.length ?? 0;
    if (!count) {
      return '';
    }
    const label = count === 1 ? 'Service' : 'Services';
    const listing = (pop.services || [])
      .map(service => (service || '').toUpperCase())
      .filter(entry => !!entry)
      .join(', ');
    return `<div class="popup-services"><span class="label">${label}:</span> ${listing}</div>`;
  }

  private getBadgeClass(pop: PopSummary): string {
    const { hasMd, hasBandwidth } = this.getServiceFlags(pop);
    if (hasMd && hasBandwidth) {
      return 'pop-badge-tooltip--mixed';
    }
    if (hasBandwidth) {
      return 'pop-badge-tooltip--bandwidth';
    }
    if (hasMd) {
      return 'pop-badge-tooltip--market-data';
    }
    return 'pop-badge-tooltip--none';
  }

  private getServiceFlags(pop: PopSummary): { hasMd: boolean; hasBandwidth: boolean } {
    const services = (pop.services || []).map(service => (service || '').toLowerCase());
    const hasBandwidth = services.includes('bandwidth');
    const hasMd = services.some(service => service && service !== 'bandwidth');
    return { hasMd, hasBandwidth };
  }
}
